import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { uiPreferencesKeys } from '~/lib/query-keys';
import type { UiPreferences } from '~/hooks/queries/use-ui-preferences';

/**
 * FRONT-2267: flip the hover-hints switch.
 *
 * Optimistic, mirroring `useSetFeedSectionOrder` — the whole point of the
 * switch is that hints stop immediately, and waiting a round trip to see that
 * makes the control feel broken. A failed write rolls back, so the switch can
 * never show a state the server does not hold.
 */
export function useSetTooltipsEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tooltipsEnabled: boolean) =>
      api.put<UiPreferences>('/api/me/ui-preferences', { tooltipsEnabled }),
    onMutate: async (tooltipsEnabled) => {
      await qc.cancelQueries({ queryKey: uiPreferencesKeys.all });
      const previous = qc.getQueryData<UiPreferences>(uiPreferencesKeys.all);
      // Spread what is cached, and invent nothing for what is not: this body
      // carries one switch. A made-up `dismissedReleaseNoteId: null` would
      // bring back a "What's new" banner the person already closed, while
      // leaving it absent keeps the banner hidden until the real read lands
      // (FRONT-2512).
      qc.setQueryData<UiPreferences>(uiPreferencesKeys.all, {
        ...previous,
        tooltipsEnabled,
      } as UiPreferences);
      return { previous };
    },
    onError: (_err, _enabled, ctx) => {
      if (ctx?.previous) qc.setQueryData(uiPreferencesKeys.all, ctx.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: uiPreferencesKeys.all });
    },
  });
}

/**
 * FRONT-2512: record that this person closed the "What's new" note.
 *
 * Optimistic for the same reason as the switch above: the banner should go
 * the moment it is closed. A failed write rolls back and says so, so the
 * banner coming back is explained rather than looking like a dead button.
 */
export function useDismissReleaseNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dismissedReleaseNoteId: string) =>
      api.put<UiPreferences>('/api/me/ui-preferences', { dismissedReleaseNoteId }),
    onMutate: async (dismissedReleaseNoteId) => {
      await qc.cancelQueries({ queryKey: uiPreferencesKeys.all });
      const previous = qc.getQueryData<UiPreferences>(uiPreferencesKeys.all);
      qc.setQueryData<UiPreferences>(uiPreferencesKeys.all, {
        ...previous,
        dismissedReleaseNoteId,
      } as UiPreferences);
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(uiPreferencesKeys.all, ctx.previous);
      toast.error("Couldn't save that. The note will show again until it saves.");
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: uiPreferencesKeys.all });
    },
  });
}
